import { createSignal, Show, Suspense } from "solid-js";
import { createQuery } from "@tanstack/solid-query";

type Todo = {
  id: number;
  title: string;
  completed: boolean;
};

const [probeTodoId, setProbeTodoId] = createSignal("2");
let autoAdvanceScheduled = false;

async function fetchTodo(id: string | number): Promise<Todo> {
  const response = await fetch(`https://jsonplaceholder.typicode.com/todos/${id}`);
  if (!response.ok) throw new Error(`Probe failed: ${response.status}`);
  return (await response.json()) as Todo;
}

export default function TanStackProbe() {
  // Intentionally isolated diagnostic: this page bypasses AppShell/auth styling to
  // exercise TanStack Query without product chrome.
  if (!autoAdvanceScheduled) {
    autoAdvanceScheduled = true;
    window.setTimeout(() => {
      setProbeTodoId("3");
    }, 1_000);
  }

  const todo = createQuery(() => ({
    queryKey: ["tanstack-probe", "todo", 1],
    queryFn: () => fetchTodo(1),
  }));

  const selectedTodo = createQuery(() => {
    const id = probeTodoId().trim();
    return {
      queryKey: ["tanstack-probe", "todo", id],
      queryFn: () => fetchTodo(id),
      enabled: Boolean(id),
    };
  });

  return (
    <main class="mx-auto max-w-3xl space-y-6 p-8">
      <header class="space-y-1">
        <h1 class="text-2xl font-semibold tracking-tight">TanStack Solid Query Probe</h1>
        <p class="text-sm text-muted-foreground">
          Isolated from AppShell, auth, /me, router permissions, and lazy routes. This is
          intentionally minimal diagnostic UI, not a customer-facing surface.
        </p>
      </header>

      <section class="space-y-3 rounded-lg border p-4">
        <h2 class="text-lg font-medium">Static Query</h2>
        <p class="text-sm">Pending: {String(todo.isPending)}</p>
        <p class="text-sm">Success: {String(todo.isSuccess)}</p>
        <Show when={todo.isError}>
          <p class="text-sm text-destructive">Error: {String(todo.error)}</p>
        </Show>
        <Suspense fallback={<p class="text-sm text-muted-foreground">Loading todo 1...</p>}>
          <Show when={todo.isSuccess && todo.data}>
            {(data) => (
              <pre class="overflow-auto rounded bg-muted p-3 text-xs">
                {JSON.stringify(data(), null, 2)}
              </pre>
            )}
          </Show>
        </Suspense>
      </section>

      <section class="space-y-3 rounded-lg border p-4">
        <h2 class="text-lg font-medium">Reactive Query</h2>
        <label class="block space-y-1 text-sm">
          <span>Todo id</span>
          <input
            class="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            value={probeTodoId()}
            onInput={(event) => setProbeTodoId(event.currentTarget.value)}
          />
        </label>
        <div class="flex items-center gap-2 text-sm">
          <span>Selected id: {probeTodoId()}</span>
          <button
            class="rounded border px-2 py-1"
            type="button"
            onClick={() => setProbeTodoId("3")}
          >
            Load 3
          </button>
          <button
            class="rounded border px-2 py-1"
            type="button"
            onClick={() => setProbeTodoId("4")}
          >
            Load 4
          </button>
        </div>
        <p class="text-sm">Pending: {String(selectedTodo.isPending)}</p>
        <p class="text-sm">Success: {String(selectedTodo.isSuccess)}</p>
        <Show when={selectedTodo.isError}>
          <p class="text-sm text-destructive">Error: {String(selectedTodo.error)}</p>
        </Show>
        <Suspense
          fallback={
            <p class="text-sm text-muted-foreground">Enter a todo id to run the reactive query.</p>
          }
        >
          <Show when={selectedTodo.isSuccess && selectedTodo.data}>
            {(data) => (
              <pre class="overflow-auto rounded bg-muted p-3 text-xs">
                {JSON.stringify(data(), null, 2)}
              </pre>
            )}
          </Show>
        </Suspense>
      </section>
    </main>
  );
}
